import React from 'react';
import { Calendar } from 'lucide-react';

function MonthlyIncentiveCard({ data, handleInputChange }) {
  return (
    <div>
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-center mb-4">
          <div className="p-2 bg-blue-100 rounded-lg mr-3">
            <Calendar className="h-5 w-5 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Monthly Incentive</h3>
        </div>

        <div className="space-y-4">
          {/* Status Toggle */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Enable Monthly Incentive</label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={data.status}
                onChange={(e) => handleInputChange('monthlyIncentive', 'status', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Incentive Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Incentive Type
            </label>
            <select
              value={data.type}
              onChange={(e) => handleInputChange('monthlyIncentive', 'type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!data.status}
            >
              <option value="multiple">Multiple</option>
              <option value="fixed">Fixed</option>
              <option value="percentage">Percentage</option>
            </select>
          </div>

          {/* Multiple Of */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Multiple Of
            </label>
            <select
              value={data.of}
              onChange={(e) => handleInputChange('monthlyIncentive', 'of', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!data.status}
            >
              <option value="salary">Salary</option>
              <option value="sale">Sale</option>
            </select>
          </div>

          {/* Value */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target {data.type.charAt(0).toUpperCase() + data.type.slice(1)} {data.type === 'multiple' ? '(X)' : data.type === 'percentage' ? '(%)' : '(₹)'}
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={data.value}
              onChange={(e) => handleInputChange('monthlyIncentive', 'value', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter value"
              disabled={!data.status}
            />
          </div>

          {/* After Target Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              After Target Percentage (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={data.afterTargetPercentage}
              onChange={(e) => handleInputChange('monthlyIncentive', 'afterTargetPercentage', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter percentage"
              disabled={!data.status}
            />
          </div>
        </div>
      </div>

      {/* Note and Calculation */}
      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
        <p className="text-sm text-green-800">
          <strong>Note:</strong> Monthly incentive has two parts: 
          <br />1. Base incentive = Monthly salary X target value
          <br />2. After target achievement, individual gets additional percentage (e.g., 5%) on every sale made beyond the target.
        </p>
        {data.status && (
          <div className="mt-3 p-2 bg-white border border-green-300 rounded">
            <p className="text-sm text-gray-700">
              <strong>Working Example:</strong>
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Base Monthly Salary: ₹18,000
            </p>
            <p className="text-sm text-gray-600">
              Base Incentive: ₹18,000 X {data.value || 0} = ₹{(18000 * (data.value || 0)).toLocaleString()}
            </p>
            {data.afterTargetPercentage > 0 && (
              <>
                <p className="text-sm text-gray-600 mt-2">
                  <strong>After Target Achievement:</strong>
                </p>
                <p className="text-sm text-gray-600">
                  Additional {data.afterTargetPercentage}% on every sale beyond target
                </p>
                <p className="text-sm text-gray-500 italic">
                  Example: If ₹10,000 extra sales after target → ₹{(10000 * (data.afterTargetPercentage / 100)).toLocaleString()} additional incentive
                </p>
              </>
            )}
            <div className="border-t pt-2 mt-2">
              <p className="text-sm font-medium text-gray-700">
                Total = Base Incentive (₹{(18000 * (data.value || 0)).toLocaleString()}) + {data.afterTargetPercentage}% of post-target sales
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MonthlyIncentiveCard;